import {
  Alert,
  Container,
  Paper,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material"
import { paymentsFailed, paymentsPaid } from "../../../core/constants"
import { PixSuccess } from "./PixSuccess"
import { BoletoSuccess } from "./BoletoSuccess"
import { useCallback, useEffect, useRef, useState } from "react"
import { IOrderCharge } from "../../../core/types/Order"
import { useLocalStorage } from "../../../core/hooks/useLocalStorage"
import { useNavigate, useParams } from "react-router-dom"
import OrderController from "../../../core/controllers/OrderController"
import Hosts from "../../../core/functions/HostFunctions"
import TagFunctions, { ITagsProp } from "../../../core/functions/TagFunctions"
import React from "react"
import { TSaleStorage } from "../../../core/types/Sale"
import HotjarFunctions from "../../../core/functions/HotjarFunctions"
import MetaFunctions from "../../../core/functions/MetaFunctions"
import GoogleFunctions from "../../../core/functions/GoogleFunctions"
import { useCheckoutContext } from "../../../core/hooks/useCheckoutContext"
import { TPaymentMethodEnum } from "../../../core/types/Product"
import AsaasSeal from "../../../components/AsaasSeal"
import { useTranslation } from "react-i18next"

export interface ISuccessOrderProps {
  paymentType: number
  QrCode?: string
  qrCodeUrl?: string
  urlRedirect?: string
  barCode?: string
  barCodeUrl?: string
  pdfUrl?: string
}

const SuccessOrder = () => {
  const { codeId } = useParams<{ codeId?: string }>()

  const { isFacebookPixelSent, addFacebookPixelSent } = useCheckoutContext()
  const { t } = useTranslation()

  const googleAds = useRef<ITagsProp[] | null>(null)
  const tagInitialDataLayer = useRef<ITagsProp | null>(null)
  const tagDataLayer = useRef<ITagsProp | null>(null)
  const tagGTM = useRef<ITagsProp | null>(null)
  const tagsHotjar = useRef<Array<HTMLScriptElement | null>>([])

  const orderCharge = useRef<IOrderCharge | undefined>(undefined)
  const [sale, setSale] = useState<TSaleStorage | undefined>(undefined)

  const [success, setSuccess] = useState(false)
  const [error, setError] = useState("")

  const [saleStorage, setSaleStorage] = useLocalStorage("afpa:sale", null)

  const navigate = useNavigate()

  let verifiedApprovalInterval: NodeJS.Timer | undefined = undefined

  const verifiedApproval = useCallback(
    async (chargeId?: string) => {
      if (verifiedApprovalInterval) {
        clearInterval(verifiedApprovalInterval)
      }

      verifiedApprovalInterval = setInterval(async () => {
        if (chargeId) {
          const charge = await OrderController.verified({ chargeId })
          if (charge) {
            if (paymentsPaid.includes(charge.status)) {
              clearInterval(verifiedApprovalInterval)
              setSuccess(true)
              orderCharge.current = charge
            } else if (paymentsFailed.includes(charge.status)) {
              clearInterval(verifiedApprovalInterval)
              setSaleStorage(null)
              setError(t("postPurchase.order.paymentError"))
            }
          }
        }
      }, 4000)
    },
    [sale]
  )

  const handleChangeSuccessClose = () => {
    if (orderCharge.current) {
      Hosts.openNavigate(`/${codeId}/success`, navigate)
    }
    setSuccess(false)
  }

  const handleChangeErrorClose = () => {
    Hosts.openNavigate(`/${codeId}`, navigate)
    setError("")
  }

  useEffect(() => {
    if (saleStorage === null) {
      Hosts.openNavigate(`/${codeId}`, navigate)
      return
    }

    setSale(saleStorage)
    verifiedApproval(saleStorage.chargeId)

    if (tagsHotjar.current.length <= 0) {
      tagsHotjar.current = HotjarFunctions.addTagsHotjar()
    }

    if (!tagInitialDataLayer.current) {
      tagInitialDataLayer.current = GoogleFunctions.initialDataLayer()
    }

    return () => {
      if (tagInitialDataLayer.current) {
        TagFunctions.removeTags(tagInitialDataLayer.current)
      }
    }
  }, [])

  useEffect(() => {
    if (sale) {
      if (!isFacebookPixelSent(sale.orderCode, "purchase")) {
        const hasPixels = MetaFunctions.hasPurchasePixels(sale, false)
        MetaFunctions.addTagPurchase(sale, false)
        MetaFunctions.sendPurchaseAPI(sale, false)
        if (hasPixels) {
          addFacebookPixelSent(sale.orderCode, "purchase")
        }
      }

      if (!googleAds.current) {
        googleAds.current = GoogleFunctions.addTagGoogleAdsPixelPurchase(sale)
      }

      if (!tagDataLayer.current) {
        tagDataLayer.current = GoogleFunctions.addDataLayerPurchase(sale)
      }
      if (!tagGTM.current) {
        tagGTM.current = GoogleFunctions.addGoogleTagManager(
          sale.googleTagManagerId
        )
      }
    }
    return () => {
      if (tagDataLayer.current) {
        TagFunctions.removeTags(tagDataLayer.current)
      }
      if (tagGTM.current) {
        TagFunctions.removeTags(tagGTM.current)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sale])

  return (
    <Container component="main" maxWidth="md" sx={{ my: 6 }} disableGutters>
      <Stack direction={"column"} spacing={2}>
        <Paper
          square={true}
          elevation={0}
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
            minHeight: "30rem",
            borderRadius: "10px",
            padding: "2rem",
            mt: 2,
          }}
        >
          <Typography
            textAlign="center"
            color="#38393B"
            fontSize="20px"
            lineHeight="24px"
            fontWeight={700}
            sx={{ mb: 2 }}
            data-cy="paymentSuccess"
          >
            {t("postPurchase.order.processed")}
          </Typography>
          {sale && sale.paymentType === TPaymentMethodEnum.Pix && (
            <PixSuccess
              qrCode={sale.code || ""}
              qrCodeUrl={sale.codeUrl || ""}
            />
          )}
          {sale && sale.paymentType === TPaymentMethodEnum.Boleto && (
            <BoletoSuccess
              barCode={sale.code || ""}
              barCodeUrl={sale.codeUrl || ""}
              pdfUrl={sale.codePdf || ""}
              redirectUrl={sale.redirectUrl}
            />
          )}
        </Paper>

        <AsaasSeal sx={{ pt: 2 }} />

        <Snackbar
          open={error !== ""}
          autoHideDuration={4000}
          onClose={handleChangeErrorClose}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            onClose={handleChangeErrorClose}
            severity="error"
            sx={{ width: "100%" }}
          >
            {error}
          </Alert>
        </Snackbar>

        <Snackbar
          open={success}
          autoHideDuration={3000}
          onClose={handleChangeSuccessClose}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            onClose={handleChangeSuccessClose}
            severity="success"
            sx={{ width: "100%" }}
          >
            {t("postPurchase.order.paymentApproved")}
          </Alert>
        </Snackbar>
      </Stack>
    </Container>
  )
}

export default SuccessOrder
